import { itemsAtom, optionGroupsAtom } from "@/atoms"
import { showToast } from "@/components/feedback"
import type { PrizeChoice } from "@/domain/marketing/prize-offer"
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog"
import { useOptimisticCart } from "@/hooks/use-optimistic-cart"
import { buildFreeProductCartItem } from "@/lib/free-product-cart-item"
import { useAtomValue } from "jotai"
import { v4 as uuid } from "uuid"

/** Where adding a free item ended: `added` when it is in the bag now, else the product page to finish its required choices. */
export type FreeItemAddition = { added: true } | { added: false; to: string }

/**
 * Puts the product of a free-item coupon in the bag at its menu price (the
 * coupon evaluation takes it off) and says so; a product with required options
 * is not guessed at, it answers with its product page instead.
 */
export function useAddFreeItem(): (choice: PrizeChoice, quantity?: number) => FreeItemAddition {
  const { addItem } = useOptimisticCart()
  const items = useAtomValue(itemsAtom)
  const optionGroups = useAtomValue(optionGroupsAtom)
  const { categories } = useBrowsingChannelCatalog()

  return (choice, quantity = 1) => {
    const productPage = { added: false, to: `/?item=${encodeURIComponent(choice.offer.id)}` } as const
    if (choice.needsOptions) return productPage
    const resolved = buildFreeProductCartItem(
      { itemId: choice.item.id, externalCode: choice.item.externalCode, name: choice.item.name },
      { items, itemOffers: [choice.offer], optionGroups, categories },
      uuid(),
    )
    if (!resolved.ok) return productPage
    const units = Math.max(1, Math.round(quantity))
    addItem({ ...resolved.cartItem, quantity: units, itemTotalPrice: resolved.cartItem.unitPrice * units })
    showToast.success(`${choice.item.name || "Seu item"} entrou na sacola`)
    return { added: true }
  }
}
